import State from 'store/State';
import {
    getStreamErrorMessageText,
    StreamStatusAction,
} from 'streamsState/store/streamStatuses/actions';
import { GetOrderColumnsMessage } from 'communication/types/order/GetOrderColumnsMessage';
import {
    selectOrdersStreamingColumns,
    selectOrdersStreamingOrderIds,
    selectStreamKey,
} from './selectors';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import {
    OrdersAction,
    OrdersActionType,
    ordersStreamSetBatchUpdateInProgressAction,
    unwatchOrderColumns,
    updateStreamingColumns,
    updateStreamingOrderIds,
} from './actions';
import { WidgetsService } from 'widgets/WidgetsService';
import { selectLayoutComponents } from 'layout/store/selectors';
import { computeRequiredStreamingColumns } from 'app/store/orders/streamingColumns';
import without from 'lodash/without';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import { showNotificationAsync } from 'notification/store/thunks';
import { arraysDiffer } from 'utils/arrays/arrayUtils';
import { NotificationType } from '@instinet-analytics/semantic-ui-components';
import { LoggingService } from '@instinet-analytics/core-lib';
import { OrdersMessageBufferService } from 'app/store/orders/OrdersMessageBuffer/OrdersMessageBufferService';
import { PortfolioService } from 'portfolio/PortfolioService';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { IndigoStore, ThunkDispatch } from 'store/store';
import { ActionsDeferredDispatcher } from 'store/ActionsDispatcher';
import { using as usingStatement } from 'using-statement';
import GetOrderColumnsRequest from 'communication/types/order/GetOrderColumnsRequest';
import { mapToMessageColumns } from 'app/store/orders/mapping';
import { ordersRequestBatchSanitizer } from 'app/store/orders/OrdersRequestBatchSanitizer';

export const OrdersStreamKey = 'Orders';

export interface OrdersStreamingServiceOptions {
    loggingService: LoggingService;
    orderMessagesBufferService: OrdersMessageBufferService;
    streamDataService: StreamDataService;
    widgetsService: WidgetsService;
    portfolioService: PortfolioService;
}

export class OrdersStreamingService {
    public store: IndigoStore | undefined;

    private readonly orderMessagesBufferService: OrdersMessageBufferService;
    private readonly streamDataService: StreamDataService;
    private readonly widgetsService: WidgetsService;
    private readonly portfolioService: PortfolioService;
    private readonly logger: Logger;

    public constructor(options: OrdersStreamingServiceOptions) {
        this.orderMessagesBufferService = options.orderMessagesBufferService;
        this.streamDataService = options.streamDataService;
        this.widgetsService = options.widgetsService;
        this.portfolioService = options.portfolioService;
        this.logger = buildLogger(
            'OrdersStreamingService',
            options.loggingService
        );

        this.streamDataService.registerRequestBatchSanitizer(
            'startOrderColumnsStream',
            ordersRequestBatchSanitizer
        );
    }

    public async startOrUpdateOrdersStream() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const state = this.store.getState();
        const dispatch: ThunkDispatch<StreamStatusAction | OrdersAction> =
            this.store.dispatch;
        const streamKey = selectStreamKey(state);
        const currentChannelId = selectChannelId(state, streamKey);

        if (!this.portfolioService.activeInputUsesOrdersStream) {
            this.logger.debug(
                'Not starting stream as active input does not use orders stream'
            );
            return;
        }

        const nextOrderIds =
            this.portfolioService.getActiveInputStreamingOrderIds();

        // If we have been passed an orderId we are in Newport and need to check whether the orderIds have changed...
        let orderIdsHaveChanged = false;
        const prevOrderIds = selectOrdersStreamingOrderIds(state);
        let orderIds = prevOrderIds;

        let payload: GetOrderColumnsRequest | undefined = undefined;

        let startAndWatchStreamRequired = true;

        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                if (nextOrderIds && nextOrderIds.length > 0) {
                    orderIds = nextOrderIds;
                    orderIdsHaveChanged = arraysDiffer(
                        prevOrderIds ?? [],
                        nextOrderIds ?? []
                    );
                    if (orderIdsHaveChanged) {
                        dispatcher.dispatch(
                            updateStreamingOrderIds(nextOrderIds ?? [])
                        );
                    }
                }

                const { nextStreamingColumns, addCols, delCols } =
                    computeColumnsDiff(state, this.widgetsService);
                const columnsHaveChanged =
                    addCols.length > 0 || delCols.length > 0;

                if (!columnsHaveChanged && !orderIdsHaveChanged) {
                    this.logger.debug(
                        'Columns and order ids unchanged: not starting/updating stream'
                    );
                    startAndWatchStreamRequired = false;
                    return;
                }

                if (orderIdsHaveChanged || addCols.length > 0) {
                    dispatcher.dispatch(
                        ordersStreamSetBatchUpdateInProgressAction()
                    );
                    this.orderMessagesBufferService.startNewBatch();
                }

                if (columnsHaveChanged) {
                    dispatcher.dispatch(
                        updateStreamingColumns(nextStreamingColumns)
                    );
                }

                if (delCols.length > 0) {
                    dispatcher.dispatch(
                        unwatchOrderColumns(nextStreamingColumns)
                    );
                }

                this.logger.debug('starting', {
                    addCols,
                    delCols,
                    orderIds,
                });

                payload = {
                    addCols: mapToMessageColumns(addCols),
                    delCols: mapToMessageColumns(delCols),
                    orderIds:
                        orderIds.length > 0 ? orderIds.join(',') : undefined,
                };
            }
        );

        if (!startAndWatchStreamRequired) {
            return;
        }

        const handlers: StreamDataHandlers<GetOrderColumnsMessage> = {
            onPending: (streamKey: string) =>
                dispatch({
                    type: OrdersActionType.ORDERS_WATCH_STREAM,
                    payload: {
                        streamKey,
                    },
                }),
            onMessage: (
                _streamKey: string,
                message: GetOrderColumnsMessage
            ) => {
                this.orderMessagesBufferService.in(message);
            },
            onError: (streamKey: string, error: StreamError) => {
                dispatch(
                    showNotificationAsync(
                        getStreamErrorMessageText(error),
                        NotificationType.Warning
                    )
                );
            },
        };

        /*
         * This is not within the using because we need the state mutated before other calls into this function from middleware
         * (so that the diffs logic works and does not do this more then necessary(
         */

        await this.streamDataService.startAndWatchStream(
            OrdersStreamKey,
            'startOrderColumnsStream',
            handlers,
            currentChannelId
        )(payload);
    }

    public async stopOrdersStream() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const state = this.store.getState();
        const streamKey = selectStreamKey(state);

        if (!streamKey) {
            return;
        }

        this.logger.debug('Stopping orders stream');

        await this.streamDataService.stopStream(streamKey);
    }
}

function computeColumnsDiff(state: State, widgetsService: WidgetsService) {
    const previousStreamingColumns = selectOrdersStreamingColumns(state);
    const components = selectLayoutComponents(state);

    const nextStreamingColumns = computeRequiredStreamingColumns(
        state,
        components,
        widgetsService
    );

    const addCols = without(nextStreamingColumns, ...previousStreamingColumns);
    const delCols = without(previousStreamingColumns, ...nextStreamingColumns);
    return { nextStreamingColumns, addCols, delCols };
}
